import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Search, Zap } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Calendar } from "../ui/calendar";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "../ui/popover";
import { TZDate } from "@date-fns/tz";
import { checkAvailability, createSnipe, fetchTldRouting, armSnipe, registerNow } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { useTimezone } from "@/lib/tz";

const REGISTRAR_OPTIONS = [
  { value: "transip", label: "TransIP.nl" },
  { value: "spaceship", label: "Spaceship.com" },
  { value: "dynadot", label: "Dynadot" },
  { value: "hostingnl", label: "Hosting.nl" },
];

export default function ScheduleForm({ onCreated }) {
  const [domain, setDomain] = useState("");
  const [registrar, setRegistrar] = useState("transip");
  const [aggressive, setAggressive] = useState(false);
  const [retryWindow, setRetryWindow] = useState(60);
  const [listOnSuccess, setListOnSuccess] = useState(false);
  const [landerPrice, setLanderPrice] = useState("");
  const [proMode, setProMode] = useState(false);
  const [raceAll, setRaceAll] = useState(false);
  const [retryMs, setRetryMs] = useState(1000);
  const [parallel, setParallel] = useState(1);
  const [intensity, setIntensity] = useState(2);
  const [date, setDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d;
  });
  const [time, setTime] = useState("09:00:00");
  const [routing, setRouting] = useState({});
  const { userTz } = useTimezone();
  const [availability, setAvailability] = useState(null);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [arming, setArming] = useState(false);
  const [plan, setPlan] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);

  useEffect(() => {
    fetchTldRouting().then((r) => setRouting(r.routing || {})).catch(() => {});
  }, []);

  // Some TLDs are registrar-locked (.nl is TransIP-only) — never let a snipe
  // be primed at a registrar that cannot serve the drop.
  const tld = (domain.trim().toLowerCase().split(".").pop() || "");
  const allowed = routing[tld] || null;
  const registrarOptions = allowed
    ? REGISTRAR_OPTIONS.filter((o) => allowed.includes(o.value))
    : REGISTRAR_OPTIONS;

  useEffect(() => {
    if (allowed && !allowed.includes(registrar)) setRegistrar(allowed[0]);
  }, [allowed, registrar]);

  // Build the UTC target from the wall-clock time in the *configured*
  // timezone (the watchlist renders in that same zone), keeping seconds —
  // a drop is won or lost on them.
  const targetIso = useMemo(() => {
    if (!date) return null;
    const [hh, mm, ss] = (time || "00:00:00").split(":").map(Number);
    const target = new TZDate(
      date.getFullYear(), date.getMonth(), date.getDate(),
      hh || 0, mm || 0, ss || 0, 0, userTz,
    );
    return target.toISOString();
  }, [date, time, userTz]);

  const handleCheck = async () => {
    if (!domain.trim()) {
      toast.error("Enter a domain first");
      return;
    }
    setChecking(true);
    try {
      const data = await checkAvailability(domain.trim().toLowerCase());
      setAvailability(data);
      setPlan(null);
      if (data.available) toast.success(`${data.domain} appears available`);
      else toast.warning(`${data.domain} looks taken — schedule for expiry`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Check failed");
    } finally {
      setChecking(false);
    }
  };

  /** One click: work out the drop time, pick the registrar cascade, arm. */
  const handleArm = async () => {
    const d = domain.trim().toLowerCase();
    if (!d.includes(".")) {
      toast.error("Enter a domain first");
      return;
    }
    setArming(true);
    setPlan(null);
    try {
      const r = await armSnipe({
        domain: d,
        list_on_success: listOnSuccess,
        lander_price: landerPrice ? Number(landerPrice) : null,
      });
      setPlan(r);
      if (r.armed) {
        toast.success(r.message);
        onCreated();
      } else {
        toast.error(r.message);
        if (r.reason === "no_prediction" || r.reason === "no_whois") setManualOpen(true);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not arm the sniper");
    } finally {
      setArming(false);
    }
  };

  const handleRegisterNow = async () => {
    setArming(true);
    try {
      const r = await registerNow({
        domain: domain.trim().toLowerCase(),
        registrar: allowed?.[0] || "transip",
        list_on_success: listOnSuccess,
        lander_price: landerPrice ? Number(landerPrice) : null,
      });
      if (r.ok) {
        toast.success(`${r.domain} registered via ${r.registrar}`);
        setPlan(null);
        setAvailability(null);
        setDomain("");
        onCreated();
      } else {
        toast.error(r.message);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setArming(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!domain.trim() || !targetIso) {
      toast.error("Domain and target time required");
      return;
    }
    if (new Date(targetIso).getTime() < Date.now() - 5000) {
      toast.error("Target time must be in the future");
      return;
    }
    setSubmitting(true);
    try {
      await createSnipe({
        domain: domain.trim().toLowerCase(),
        target_time: targetIso,
        registrar,
        retry_mode: aggressive ? "aggressive" : "single",
        retry_window_seconds: Number(retryWindow) || 60,
        list_on_success: listOnSuccess,
        lander_price: landerPrice ? Number(landerPrice) : null,
        race_all_registrars: raceAll,
        retry_interval_ms: Math.max(50, Number(retryMs) || 1000),
        parallel_attempts: Math.max(1, Math.min(5, Number(parallel) || 1)),
        burst_intensity: Math.max(1, Math.min(3, Number(intensity) || 2)),
      });
      toast.success(`Snipe primed for ${domain}`);
      setDomain("");
      setAvailability(null);
      setLanderPrice("");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to schedule");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="border-b border-[#eaecf0] px-6 py-5 bg-white"
      data-testid="schedule-form"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline">Schedule</div>
          <div className="font-display text-lg font-medium mt-0.5">New Snipe</div>
        </div>
        <Zap className="w-4 h-4 text-[#03142f]" strokeWidth={1.5} />
      </div>

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="domain" className="overline">Domain</Label>
          <div className="flex gap-2">
            <Input
              id="domain"
              data-testid="domain-input"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="example.nl"
              className="rounded-sm font-mono text-sm focus-visible:ring-1 focus-visible:ring-[#03142f] focus-visible:border-[#03142f]"
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleCheck}
              disabled={checking}
              data-testid="check-availability-btn"
              className="rounded-sm shrink-0"
            >
              <Search className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
              {checking ? "…" : "Check"}
            </Button>
          </div>
          {availability && (
            <div
              className="mt-2 border border-[#eaecf0] p-3 rounded-sm bg-[#FAFAFA] text-xs font-mono"
              data-testid="availability-result"
            >
              <div className="flex justify-between mb-1">
                <span>{availability.domain}</span>
                <span
                  className="px-1.5 py-0.5 rounded-sm text-[10px]"
                  style={{
                    background: availability.available ? "#ECFDF5" : "#FFF1F2",
                    color: availability.available ? "#047857" : "#BE123C",
                  }}
                >
                  {availability.available ? "AVAILABLE" : "TAKEN"}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[#525252]">
                <div>TransIP reg: €{availability.transip_price}</div>
                <div>Spaceship reg: €{availability.spaceship_price}</div>
              </div>
              {availability.whois && !availability.whois.error && (availability.whois.registrar_name || availability.whois.created || availability.whois.expires) && (
                <div
                  className="mt-3 pt-3 border-t border-[#eaecf0] space-y-1 text-[11px]"
                  data-testid="whois-result"
                >
                  <div className="overline text-[9px] text-[#525252]">WHOIS · WhoisJson</div>
                  {availability.whois.registrar_name && (
                    <div><span className="text-[#525252]">Registrar:</span> {availability.whois.registrar_name}</div>
                  )}
                  {availability.whois.created && (
                    <div><span className="text-[#525252]">Created:</span> {availability.whois.created}</div>
                  )}
                  {availability.whois.expires && (
                    <div>
                      <span className="text-[#525252]">Expires:</span> {availability.whois.expires}
                      {availability.whois.days_until_expiration != null && (
                        <span className={`ml-2 px-1 rounded-sm text-[10px] ${availability.whois.is_expiring_soon ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#F4F4F5] text-[#525252]"}`}>
                          {availability.whois.days_until_expiration}d left
                        </span>
                      )}
                    </div>
                  )}
                  {availability.whois.age_years != null && (
                    <div><span className="text-[#525252]">Age:</span> {availability.whois.age_years}y</div>
                  )}
                  {availability.whois.dns_provider && (
                    <div><span className="text-[#525252]">DNS:</span> {availability.whois.dns_provider}</div>
                  )}
                  {availability.whois.status && availability.whois.status.length > 0 && (
                    <div className="text-[10px] text-[#525252] truncate" title={availability.whois.status.join(", ")}>
                      {availability.whois.status.slice(0, 2).join(" · ")}
                    </div>
                  )}
                </div>
              )}
              {availability.whois?.error && (
                <div className="mt-2 pt-2 border-t border-[#eaecf0] text-[10px] text-[#BE123C]" data-testid="whois-error">
                  WHOIS: {availability.whois.error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ---- one-click arm ---- */}
        {(availability || plan) && (
          <div className="space-y-2" data-testid="snipe-plan">
            {availability?.predicted_drop && (
              <div className="text-[11px] font-mono text-[#525252]">
                Drop estimate ·{" "}
                <span className="text-[#03142f]">
                  {formatDateTime(availability.predicted_drop.drop_at_utc, userTz)}
                </span>{" "}
                · {availability.predicted_drop.confidence} confidence
              </div>
            )}
            {availability?.registrar_cascade?.length > 0 && (
              <div className="text-[11px] font-mono text-[#525252]" data-testid="cascade-preview">
                Registrars ·{" "}
                {availability.registrar_cascade.map((r) => r.toUpperCase()).join(" → ")}
              </div>
            )}
            {availability?.available || plan?.reason === "available_now" ? (
              <Button
                type="button"
                onClick={handleRegisterNow}
                disabled={arming}
                data-testid="register-now-inline-btn"
                className="w-full rounded-sm bg-[#047857] hover:bg-[#065f46] text-white font-medium"
              >
                <Zap className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.8} />
                {arming ? "Registering…" : "It's free — register now"}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleArm}
                disabled={arming}
                data-testid="arm-snipe-btn"
                className="w-full rounded-sm bg-[#03142f] hover:bg-[#0b274d] text-white font-medium"
              >
                <Zap className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.8} />
                {arming ? "Arming…" : "Snipe it"}
              </Button>
            )}
            {plan && (
              <p
                data-testid="arm-result"
                className={`text-[11px] font-mono ${plan.armed ? "text-[#047857]" : "text-[#B45309]"}`}
              >
                {plan.armed
                  ? `Armed · drops ${formatDateTime(plan.drop_at_utc, userTz)} · ${plan.registrar_cascade
                      .map((r) => r.toUpperCase())
                      .join(" → ")} · ${plan.confidence} confidence`
                  : plan.message}
              </p>
            )}
          </div>
        )}

        {/* ---- manual timing (advanced) ---- */}
        <div className="pt-4 border-t border-[#eaecf0]">
          <button
            type="button"
            onClick={() => setManualOpen(!manualOpen)}
            data-testid="manual-timing-toggle"
            className="text-xs font-mono uppercase tracking-widest text-[#525252] hover:text-[#03142f] flex items-center gap-1.5"
          >
            <span className={`transition-transform inline-block ${manualOpen ? "rotate-90" : ""}`}>›</span>
            Manual timing
          </button>
        </div>

        {manualOpen && (
        <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1.5">
            <Label className="overline">Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="date-picker-btn"
                  className="w-full flex items-center gap-2 px-3 py-2 border border-[#eaecf0] rounded-sm text-sm font-mono hover:bg-[#FAFAFA]"
                >
                  <CalendarIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {date ? format(date, "yyyy-MM-dd") : "select"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0 rounded-sm w-auto" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => d && setDate(d)}
                  disabled={(d) => d < new Date(new Date().toDateString())}
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="time" className="overline truncate block">
              Time · {String(userTz).split("/").pop().replace(/_/g, " ")}
            </Label>
            <Input
              id="time"
              type="time"
              step="1"
              data-testid="time-input"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="rounded-sm font-mono text-sm"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="overline">Registrar</Label>
          <Select value={registrar} onValueChange={setRegistrar}>
            <SelectTrigger data-testid="registrar-select" className="rounded-sm font-mono text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-sm">
              {registrarOptions.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`registrar-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {allowed && (
            <p className="text-[11px] font-mono text-[#B45309]" data-testid="registrar-tld-lock">
              .{tld} is {allowed.join(" / ")}-only — other registrars can't take this drop.
            </p>
          )}
        </div>

        <div className="flex items-start justify-between gap-4 pt-1">
          <div>
            <Label className="text-sm font-medium">Aggressive retry</Label>
            <p className="text-xs text-[#525252] mt-0.5">
              Retry every second for the window below until success.
            </p>
          </div>
          <Switch
            checked={aggressive}
            onCheckedChange={setAggressive}
            data-testid="aggressive-switch"
          />
        </div>

        {aggressive && (
          <div className="space-y-1.5">
            <Label htmlFor="retry" className="overline">Retry window (seconds)</Label>
            <Input
              id="retry"
              type="number"
              min={5}
              max={600}
              value={retryWindow}
              onChange={(e) => setRetryWindow(e.target.value)}
              data-testid="retry-window-input"
              className="rounded-sm font-mono text-sm"
            />
          </div>
        )}

        <div className="flex items-start justify-between gap-4 pt-1 border-t border-[#eaecf0] pt-4">
          <div>
            <Label className="text-sm font-medium">List for sale on success</Label>
            <p className="text-xs text-[#525252] mt-0.5">
              Auto-publish a lander page when this snipe wins.
            </p>
          </div>
          <Switch
            checked={listOnSuccess}
            onCheckedChange={setListOnSuccess}
            data-testid="list-on-success-switch"
          />
        </div>

        {listOnSuccess && (
          <div className="space-y-1.5">
            <Label htmlFor="lander-price" className="overline">Lander price (€, optional)</Label>
            <Input
              id="lander-price"
              type="number"
              min={0}
              step="1"
              placeholder="Uses default if empty"
              value={landerPrice}
              onChange={(e) => setLanderPrice(e.target.value)}
              data-testid="lander-price-input"
              className="rounded-sm font-mono text-sm"
            />
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-[#eaecf0]">
          <button
            type="button"
            onClick={() => setProMode(!proMode)}
            data-testid="pro-mode-toggle"
            className="text-xs font-mono uppercase tracking-widest text-[#525252] hover:text-[#03142f] flex items-center gap-1.5"
          >
            <span className={`transition-transform inline-block ${proMode ? "rotate-90" : ""}`}>›</span>
            Pro sniping
          </button>
          {(raceAll || retryMs !== 1000 || parallel > 1) && (
            <span className="text-[10px] font-mono px-1.5 py-0.5 bg-[#03142f] text-white rounded-sm">ACTIVE</span>
          )}
        </div>
        {proMode && (
          <div className="space-y-4 border border-[#eaecf0] p-3 rounded-sm bg-[#FAFAFA]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label className="text-sm font-medium">Race all registrars</Label>
                <p className="text-xs text-[#525252] mt-0.5">
                  Fire at every configured registrar in parallel. First win takes it.
                </p>
              </div>
              <Switch checked={raceAll} onCheckedChange={setRaceAll} data-testid="race-all-switch" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="overline">Burst interval (ms)</Label>
                <Input
                  type="number"
                  min={50}
                  max={5000}
                  step={50}
                  value={retryMs}
                  onChange={(e) => setRetryMs(e.target.value)}
                  data-testid="retry-ms-input"
                  className="rounded-sm font-mono text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="overline">Parallel attempts</Label>
                <Input
                  type="number"
                  min={1}
                  max={5}
                  value={parallel}
                  onChange={(e) => setParallel(e.target.value)}
                  data-testid="parallel-input"
                  className="rounded-sm font-mono text-sm"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="overline">Burst intensity</Label>
              <div className="flex gap-1" data-testid="burst-intensity-group">
                {[
                  { v: 1, label: "Light", waves: "3 waves" },
                  { v: 2, label: "Normal", waves: "8 waves" },
                  { v: 3, label: "Turbo", waves: "20 waves" },
                ].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setIntensity(o.v)}
                    data-testid={`intensity-${o.v}`}
                    className={`flex-1 text-[11px] font-mono uppercase px-2 py-2 border ${
                      intensity === o.v
                        ? "border-[#03142f] bg-[#03142f] text-white"
                        : "border-[#eaecf0] text-[#525252] hover:bg-[#FAFAFA]"
                    }`}
                  >
                    <div>{o.label}</div>
                    <div className="text-[9px] opacity-70">{o.waves}</div>
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[10px] font-mono text-[#A1A1AA] leading-relaxed">
              Aggressive + 200ms burst + 3 parallel + race + turbo = up to <b>240 attempts</b> in the
              first 500ms after drop. Use for the highest-value drops.
            </p>
          </div>
        )}

        <Button
          type="submit"
          disabled={submitting}
          data-testid="schedule-submit-btn"
          className="w-full rounded-sm bg-[#03142f] hover:bg-[#0b274d] text-white font-medium"
        >
          {submitting ? "Priming…" : "Prime snipe"}
        </Button>
        </div>
        )}
      </div>
    </form>
  );
}
